// Resolve the API origin from the host the SPA is served on. The viewer and the
// API are separate subdomains under one registrable domain, so they share Lax
// cookies (same-site) while still being cross-ORIGIN for fetch/CORS:
//   app.yoreflix.uk   -> api.yoreflix.uk    (staging)
//   <x>.yoreflix.com  -> api.yoreflix.com   (production)
// A VITE_API_BASE override wins (local dev / throwaway hosts). Note: from a
// non-yoreflix origin (e.g. localhost) the session cookies are cross-SITE and
// will NOT flow — the auth slice is only faithful when served from *.yoreflix.*.

const NAMESPACE = 'wp-json/yoreflix/v1'

/**
 * True when `hostname` is the production (.com) registrable domain — else the .uk staging rig.
 *
 * Pure, and exported, because the host is no longer only an API-routing question: it is also
 * what tells the viewer whether it is serving a customer or a test rig (see
 * `apps/web/src/boot/diagnostics.ts`). Same split as `measurementIdForHost` in analytics/ga —
 * the decision is testable without a window, and the impure wrapper below just supplies one.
 *
 * ⚠ Anchored on a leading dot, so a host that merely CONTAINS the string — `yoreflix.com.evil.test`
 * — is not production.
 */
export function isProductionHostname(hostname: string): boolean {
  return hostname.endsWith('.yoreflix.com') || hostname === 'yoreflix.com'
}

/** True when served from the production (.com) registrable domain — else the .uk staging rig. */
function isComHost(): boolean {
  return isProductionHostname(window.location.hostname)
}

export function apiBaseUrl(): string {
  const override = import.meta.env.VITE_API_BASE as string | undefined
  if (override) return override.replace(/\/+$/, '')
  // Default (staging + local fallback) targets the live staging API.
  return isComHost() ? 'https://api.yoreflix.com' : 'https://api.yoreflix.uk'
}

/**
 * The signup funnel's origin, derived from the host (like apiBaseUrl) so no build-time env var is
 * needed — the .uk rig links to signup.yoreflix.uk, .com to signup.yoreflix.com. VITE_SIGNUP_URL
 * overrides (local/throwaway). Used by the viewer's "Create an account" link.
 */
export function signupUrl(): string {
  const override = import.meta.env.VITE_SIGNUP_URL as string | undefined
  if (override) return override.replace(/\/+$/, '')
  return isComHost() ? 'https://signup.yoreflix.com' : 'https://signup.yoreflix.uk'
}

/**
 * The viewer app's origin, derived from the host. Used by the signup app (back-to-signin + the
 * free-tier landing), which runs on a different subdomain from the viewer. VITE_APP_URL overrides.
 */
export function viewerUrl(): string {
  const override = import.meta.env.VITE_APP_URL as string | undefined
  if (override) return override.replace(/\/+$/, '')
  return isComHost() ? 'https://app.yoreflix.com' : 'https://app.yoreflix.uk'
}

/**
 * The MARKETING site's origin — the apex, derived from the host like everything else here:
 * `*.yoreflix.com` → `https://yoreflix.com`, otherwise the `.uk` rig. `VITE_MARKETING_URL`
 * overrides for local/throwaway hosts.
 *
 * ⚠ Used where a signed-OUT person should land: they have no account any more, so sending them to
 * the app's sign-in screen is sending them to a door they just closed. The apex is the front of
 * the product.
 *
 * ⛔⛔ THE APEX IS SESSION-GATED. The gateway Worker bounces anyone arriving at `yoreflix.com/`
 * WITH a `yoreflix_session` cookie straight back to `app.yoreflix.com`. So navigating here is only
 * safe once the cookie is genuinely gone — the server has to have accepted the sign-out. Send
 * somebody here while their cookie survives and they bounce back into the app with no way out.
 * See `signOut` in apps/web/src/auth/AuthProvider.tsx, which is why that redirect is conditional.
 */
export function marketingUrl(): string {
  const override = import.meta.env.VITE_MARKETING_URL as string | undefined
  if (override) return override.replace(/\/+$/, '')
  return isComHost() ? 'https://yoreflix.com' : 'https://yoreflix.uk'
}

/** Full URL for a yoreflix/v1 path, e.g. apiUrl('auth/session'). */
export function apiUrl(path: string): string {
  return `${apiBaseUrl()}/${NAMESPACE}/${path.replace(/^\/+/, '')}`
}
